import { inject, Injectable } from '@angular/core'
import { map, shareReplay } from 'rxjs/operators'
import {
  AssistantSummaryReport,
  ClientSummaryReport,
  ContractMonthlySummary,
  RoleAssistantSummaryReport,
} from '../../models/financialReporting'
import { FeathersService } from '../feathers.service'
import { Assistance } from '../../models/assistance'
import { DateRange, DateUtils } from '../date-utils.service'
import { AssistanceService } from './assistance/assistance.service'
import { Observable } from 'rxjs'
import { CustomMethod } from '@feathersjs/client'
import {
  AssistantContractDay,
  AssistantContractWithTimeSummaryWithTotals,
} from '../../models/assistantContractWithTimeSummary'

@Injectable({
  providedIn: 'root',
})
export class FinanceService {
  private feathers = inject(FeathersService)
  private assistanceService = inject(AssistanceService)

  private TAG: string = 'FinanceService'

  /**
   * Get summary report for user with assistant role
   * @param range From when to when
   * @param assistantId For which assistant
   */
  getRoleAssistantSummaryReports(range: DateRange, assistantId: number): Observable<RoleAssistantSummaryReport[]> {
    return this.feathers
      .find(RoleAssistantSummaryReport, {
        command: 'roleAssistantFinance',
        range,
        assistantId,
      })
      .pipe(
        map((reports) => {
          for (const report of reports) {
            report.realizedFrom = report.realizedFrom ? new Date(report.realizedFrom) : undefined
            report.realizedTo = report.realizedTo ? new Date(report.realizedTo) : undefined
          }
          return reports
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
      )
  }

  async billAssistances(assistances: Assistance[]) {
    for (const a of assistances) {
      if (!a.isBilled) {
        a.isBilled = true
        // TODO multipatch
        await this.assistanceService.patchNonRecurrently({
          id: a.id,
          isBilled: true,
        })
      }
    }
  }

  getClientSummaryReports(range: DateRange) {
    return this.feathers.find(ClientSummaryReport, { command: 'summary', range })
  }

  getAssistantSummaryReports(range: DateRange) {
    return this.feathers.find(AssistantSummaryReport, {
      command: 'summary',
      range,
    })
  }

  getContractMonthlySummaries(range: DateRange): Observable<ContractMonthlySummary[]> {
    return this.feathers.find(ContractMonthlySummary, {
      month: range.from.getMonth() + 1,
      year: range.from.getFullYear(),
    })
  }

  async saveContractMonthlyReward(params: {
    rewardId: number | null
    contractId: number
    month: number
    year: number
    bonusCZK: number | null
    personalEvaluationCZK: number | null
  }): Promise<{ id: number }> {
    const result = await this.feathers.patchsert('contract-monthly-rewards', {
      id: params.rewardId ?? undefined,
      contractId: params.contractId,
      month: params.month,
      year: params.year,
      bonusCZK: params.bonusCZK,
      personalEvaluationCZK: params.personalEvaluationCZK,
    })
    return result as { id: number }
  }

  recalculateUnbilledReportsForMonth(year: number, jsMonth: number, jobHandle: string) {
    this.feathers.postCommand('finance-recalculateByRange', { year, jsMonth }, { jobHandle })
  }

  recalculateSelectedReports(assistanceIds: number[], jobHandle: string) {
    this.feathers.postCommand('finance-recalculateByAssistanceIds', { assistanceIds }, { jobHandle })
  }

  getEditorAssistanceFinancialReportsForAssistance(assistanceId: number) {
    return this.feathers
      .serviceWithCustomMethods<{
        getSumsForAssistance: CustomMethod
      }>('financial-reports')
      .getSumsForAssistance(assistanceId)
  }

  getFinanceAssistantSummaryWithDays(assistantId: number, dateRange: DateRange) {
    const createEmptyDay = (date: Date): AssistantContractDay => ({
      realizedDate: date,
      activityTimeSum: 0,
      transitTimeSum: 0,
      totalSum: 0,
    })

    return this.feathers
      .find(AssistantContractWithTimeSummaryWithTotals, {
        dateRange,
        assistantId,
      })
      .pipe(
        map((contracts) => {
          contracts.map((contract) => {
            // Create new dates, so functions such as getTime works properly
            contract.validFrom = new Date(contract.validFrom)
            contract.validTo = new Date(contract.validTo)
            contract.days.forEach((day) => {
              day.realizedDate = new Date(day.realizedDate)
            })
            // Sort returned days
            contract.days.sort((a, b) => a.realizedDate.getTime() - b.realizedDate.getTime())
            // Fill missing days
            const daysInRange = DateUtils.getDaysInRange(dateRange)
            contract.days = daysInRange.map((date, index) => {
              const matchingDay = contract.days.find(
                (day) => DateUtils.startOfDay(day.realizedDate).getTime() === DateUtils.startOfDay(date).getTime(),
              )

              return matchingDay || createEmptyDay(date)
            })
            return contract
          })
          // Sort contracts
          contracts.sort((a, b) => {
            return a.validFrom.getTime() - a.validFrom.getTime() // prefer the contract with sooner validFrom
          })
          return contracts
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
      )
  }
}
